// -----------------------------
// STRIPE DONATION COMPONENT
// -----------------------------
// This component allows users to donate either a one-time amount or choose
// between recurring monthly donations ($25, $50, or $100).
// It uses Stripe's embedded Buy Button and dynamically loads the script on mount.

// -----------------------------
// IMPORTS
// -----------------------------
import clsx from "clsx";
import React, { useState, useEffect } from "react";
import { DonationCard, SliderTab } from "./components";

// -----------------------------
// STRIPE BUY BUTTON IDS
// -----------------------------
// These are preset button IDs from Stripe that link to the actual donation options.
const btn25 = "buy_btn_1R9ABDHh1sadRbzIM3EriSUJ";
const btn50 = "buy_btn_1R9APqHh1sadRbzIHNxZW03H";
const btn100 = "buy_btn_1R9ATtHh1sadRbzI2CGo4hps";

export default function Stripe() {
    // -----------------------------
    // STATE SETUP
    // -----------------------------
    // Tracks if the user wants a one-time donation vs. recurring.
    const [oneTimePayment, setOneTimePayment] = useState(false);
    // Tracks which recurring option is selected (1 = $25, 2 = $50, 3 = $100).
    const [selectedRecurring, setRecurring] = useState(2);

    // -----------------------------
    // LOAD STRIPE SCRIPT ON MOUNT
    // -----------------------------
    // This injects Stripe’s Buy Button script only when the component is loaded.
    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/buy-button.js";
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    // -----------------------------
    // DONATION CARD PROPS
    // -----------------------------
    // Prepares settings for each donation option: $25, $50, and $100 monthly.
    const re25Props = {
        setSelected: () => setRecurring(1),
        selected: selectedRecurring === 1,
        btnId: btn25,
        monthly: 25,
        annual: 300,
    };

    const re50Props = {
        setSelected: () => setRecurring(2),
        selected: selectedRecurring === 2,
        btnId: btn50,
        monthly: 50,
        annual: 600,
    };

    const re100Props = {
        setSelected: () => setRecurring(3),
        selected: selectedRecurring === 3,
        btnId: btn100,
        monthly: 100,
        annual: 1200,
    };

    // -----------------------------
    // SLIDER TOGGLE PROPS
    // -----------------------------
    // Controls the tab switch between one-time and monthly options.
    const sliderProps = {
        oneTimePayment,
        onTabClick: () => setOneTimePayment(!oneTimePayment),
    };

    // -----------------------------
    // MAIN RENDER LOGIC
    // -----------------------------
    // If the user selects one-time donation, show Stripe's one-time button.
    // Otherwise, show the three recurring donation options.
    return (
        <div className="flex w-full flex-col items-center">
            <SliderTab {...sliderProps} />
            {oneTimePayment ? (
                <div className={selectedStyle}>
                    <stripe-buy-button
                        buy-button-id="buy_btn_1R99YdHh1sadRbzIi4csM22a"
                        publishable-key={process.env.REACT_APP_STRIPE_PUBLIC_KEY}
                    ></stripe-buy-button>
                </div>
            ) : (
                <div className="flex flex-col justify-around lg:flex-row">
                    {/* $25/MONTH */}
                    <DonationCard {...re25Props} />
                    {/* $50/MONTH */}
                    <DonationCard {...re50Props} />
                    {/* $100/MONTH */}
                    <DonationCard {...re100Props} />
                </div>
            )}
        </div>
    );
}

// -----------------------------
// STYLES
// -----------------------------
// Reusable style setup for donation cards and the selected one-time option
const cardBase = clsx(
    "rounded-xl px-4 flex flex-col items-center mx-2 my-2 lg:my-0 justify-center w-[320px] md:w-[340px]",
);
export const selectedStyle = clsx(
    cardBase + " bg-[#f9f9f9] pt-8 pb-4 shadow-lg h-[300px]",
);
